import React, { useEffect, useState } from 'react';
import { useSelectedVersion } from '@client/store/ui';
import { useProposals } from '@features/proposals/use-proposals.hooks';
import { isReportCoverSettled } from '@client/helpers/report-pdf-ready.helpers';
import {
  REPORT_PDF_CHART_SETTLE_MS,
  REPORT_PDF_COVER_UNMOUNT_WAIT_MS,
  getReportPdfCoverImageWaitMs,
  getReportPdfCoverMapWaitMs,
  getReportPdfPageWaitMs,
  getReportPdfPrintCss,
  hasInjectedCoverMapQuery,
} from '@shared/helpers/report-pdf-generation.helpers';
import { useConfig } from '@client/providers/ConfigProvider';
import { ReportView } from '../MainView/components/ProjectView/components/ReportView/ReportView';
import { ReportPdfPrintProvider } from './report-pdf-print.context';
import {
  discardCoverMapCanvases,
  finalizeCoverMapForPdf,
  hasMinimumReportPages,
  hasNoCoverMapCanvas,
  isCoverMapReady,
  REPORT_PDF_READY_ATTR,
  waitUntil,
} from './report-pdf-ready.helpers';

/** Cover, overview, and summary are always rendered before proposal pages. */
const MIN_REPORT_PAGES_FOR_PDF = 3;

type WindowWithReportReady = Window & { __reportReady?: boolean };

/**
 * Print sheet setup for the report. Owned by the app (not injected by the PDF
 * renderer) so a manual Cmd+P on `?print=1` produces the same sheets as the
 * Browserless export. The app shell keeps `html`/`body` at viewport height with
 * clipped overflow, which drops everything after the first sheet unless undone.
 */
const PRINT_CSS = getReportPdfPrintCss();

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * Full-page report used by Browserless (`?print=1`) and local PDF debug URLs.
 *
 * PDF-ready pipeline (DEV-3533 / DEV-3562):
 * 1. Wait until report pages have mounted (version data is in the DOM).
 * 2. Wait for the stored cover-image fetch to settle so Map3d is not mounted
 *    while that request is still in flight.
 * 3. Wait for the cover Map3d to reach Mapbox idle with the 3D building
 *    (empty-design idle is ignored), unless a client snapshot will be
 *    injected (`coverMap=injected`). The Mapbox timer starts only after
 *    step 2. Headless Chrome often never paints WebGL, so Export PDF
 *    captures the map in the user's browser instead.
 * 4. Brief extra settle for charts and three.js frames after idle.
 * 5. Snapshot an idle cover canvas to PNG, or discard leftover WebGL so
 *    Chrome's `page.pdf()` print pipeline is not asked to rasterize it.
 * 6. Unmount Map3d so it cannot recreate a canvas, then set
 *    `html[data-report-pdf-ready]`.
 */
export const PrintView: React.FC = () => {
  const [{ environment }] = useConfig();
  const selectedVersion = useSelectedVersion();
  const proposals = useProposals();
  const skipLiveCoverMap = hasInjectedCoverMapQuery(window.location.search);
  const [isCoverFrozen, setIsCoverFrozen] = useState(false);

  useEffect(() => {
    const win = window as WindowWithReportReady;
    win.__reportReady = false;
    document.documentElement.removeAttribute(REPORT_PDF_READY_ATTR);
    setIsCoverFrozen(false);

    if (!selectedVersion) return;

    const signal = { cancelled: false };

    const signalReady = () => {
      if (signal.cancelled) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (signal.cancelled) return;
          win.__reportReady = true;
          document.documentElement.setAttribute(REPORT_PDF_READY_ATTR, 'true');
        });
      });
    };

    void (async () => {
      const pagesReady = await waitUntil(
        () => hasMinimumReportPages(document, MIN_REPORT_PAGES_FOR_PDF),
        { timeoutMs: getReportPdfPageWaitMs(environment), signal },
      );
      if (signal.cancelled) return;
      if (!pagesReady) return;
      // Fallback fonts are tighter than Roboto. Wait so export matches the app.
      await document.fonts?.ready?.catch(() => undefined);
      if (signal.cancelled) return;
      const coverImageSettled = await waitUntil(() => isReportCoverSettled(), {
        timeoutMs: getReportPdfCoverImageWaitMs(environment),
        signal,
      });
      if (signal.cancelled) return;
      // Map3d only mounts after the cover-image fetch settles. Skip the idle
      // wait when a client snapshot will be injected — headless Mapbox is the
      // timeout path from DEV-3562.
      if (coverImageSettled && !skipLiveCoverMap) {
        await waitUntil(() => isCoverMapReady(document.documentElement), {
          timeoutMs: getReportPdfCoverMapWaitMs(environment),
          signal,
        });
        if (signal.cancelled) return;
      }
      await delay(REPORT_PDF_CHART_SETTLE_MS);
      if (signal.cancelled) return;
      await finalizeCoverMapForPdf(document, document.documentElement);
      if (signal.cancelled) return;
      setIsCoverFrozen(true);
      await waitUntil(() => hasNoCoverMapCanvas(document), {
        timeoutMs: REPORT_PDF_COVER_UNMOUNT_WAIT_MS,
        signal,
      });
      if (signal.cancelled) return;
      discardCoverMapCanvases(document);
      signalReady();
    })();

    return () => {
      signal.cancelled = true;
    };
  }, [environment, selectedVersion, proposals.length, skipLiveCoverMap]);

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div
        style={{
          width: '100vw',
          minHeight: '100vh',
          background: '#fff',
          overflow: 'visible',
        }}
      >
        <ReportPdfPrintProvider
          isCoverFrozen={isCoverFrozen}
          skipLiveCoverMap={skipLiveCoverMap}
        >
          <ReportView hideToolbar />
        </ReportPdfPrintProvider>
      </div>
    </>
  );
};
